"use client";

import { format, isDate, isValid } from "date-fns";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useEffect } from "react";

import { cn } from "~/../lib/utils";
import { Button } from "~/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { toast } from "sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarIcon } from "lucide-react";
import { DateRangePickerCalendar } from "~/components/common/date-range-picker-calendar";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import type { DateRangePresetUnit, RelativeDateRange } from "~/types/ui";
import {
  convertRelativeDateRange,
  getRelativeDateRangeFromPreset,
  groupedDateRangePresets,
  nowRegex,
  relativeRegex,
  toStorableRange,
} from "~/utils/date-util";
import { Input } from "../ui";
const isValidDateInput = (val: string) => isValid(new Date(val));

const FormSchema = z.object({
  from: z.union([
    z.date({
      required_error: "dateTime.fromRequired",
    }),
    z
      .string()
      .refine(
        (val) =>
          nowRegex.test(val) ||
          relativeRegex.test(val) ||
          isValidDateInput(val),
        { message: "dateTime.invalidRelative" },
      ),
  ]),

  to: z.union([
    z.date({
      required_error: "dateTime.toRequired",
    }),
    z
      .string()
      .refine(
        (val) =>
          nowRegex.test(val) ||
          relativeRegex.test(val) ||
          isValidDateInput(val),
        { message: "dateTime.invalidRelative" },
      ),
  ]),
});

const normalizeDateValue = (value: unknown): string => {
  if (isDate(value) && isValid(value)) {
    return format(value, "yyyy/MM/dd HH:mm");
  }

  if (typeof value === "string") {
    return value;
  }

  return "";
};

/**
 * A boundary in the form `FormSchema` actually accepts: relative tokens stay strings,
 * everything parseable becomes a real `Date`.
 *
 * Seeding went through `getMixedDateRange`, which renders an absolute boundary as its
 * display string ("2026/09/15 10:00"). `normalizeDateValue` draws that identically to a
 * `Date`, so the field looked right — but the schema takes a `Date` or a relative token
 * and nothing else, and only blurring the field converted it. Open the picker on an
 * absolute range, change one end via the calendar, submit: the end you never focused was
 * still a display string and failed validation on a field the user hadn't touched.
 *
 * Also what a field's blur runs, so seeding and typing normalise input the same way.
 */
const toFormValue = (value: Date | string): Date | string => {
  if (isDate(value)) return isValid(value) ? value : "";
  if (typeof value !== "string") return "";
  if (nowRegex.test(value) || relativeRegex.test(value)) return value;

  const parsed = new Date(value);
  return isValid(parsed) ? parsed : value;
};

export function DateRangePicker({
  value,
  setRange,
  onClose,
}: {
  value: RelativeDateRange;
  setRange: (result: RelativeDateRange) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
  });

  useEffect(() => {
    form.setValue("from", toFormValue(value.from));
    form.setValue("to", toFormValue(value.to));
  }, [value]);

  function onSubmit(data: z.infer<typeof FormSchema>) {
    // `FormSchema` accepts either a relative token or a real `Date` (what the calendar
    // hands back), so the submitted values are only *sometimes* the strings this
    // component's `setRange` prop is typed to emit. Normalizing here rather than casting
    // keeps that promise honest for every caller — otherwise a calendar-picked range
    // reaches zod schemas typed `z.string()` and URL params as `Date` objects.
    const relativeRange: RelativeDateRange = toStorableRange(data);
    setRange(relativeRange);

    const dateRange = convertRelativeDateRange(relativeRange);
    toast.success(
      t("dateTime.selectedRange", {
        from: format(dateRange.from, "PPPP HH:mm"),
        to: format(dateRange.to, "PPPP HH:mm"),
      }),
    );
    onClose();
  }

  function handlePresetClick(value: number, unit: DateRangePresetUnit) {
    const range = getRelativeDateRangeFromPreset(value, unit);
    form.setValue("from", range.from);
    form.setValue("to", range.to);
  }

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          // Popover content is portaled to document.body, but React still bubbles
          // synthetic events through the *component* tree rather than the DOM tree —
          // so without this, submitting this form also bubbles up to (and submits)
          // any outer <form> this picker happens to be rendered inside of.
          e.stopPropagation();
          form.handleSubmit(onSubmit)(e);
        }}
        className="space-y-2"
      >
        <div className="p-3">
          <DateTimeFormField
            name="from"
            title={t("dateTime.from")}
            form={form}
          />
          <DateTimeFormField name="to" title={t("dateTime.to")} form={form} />
          <div className="mt-2">
            {groupedDateRangePresets.map((group, i) => (
              <div className="rounded-lg border bg-muted/30 p-2 mb-2">
                <div className="my-1 flex items-center gap-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t("dateTime.presets.units." + group.category)}
                  </h3>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <div key={i} className="grid grid-cols-4 gap-1.5">
                  {group.items.map((d) => (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="h-7 px-2 text-xs hover:bg-accent"
                      onClick={() => handlePresetClick(d.value, d.unit)}
                      type="button"
                    >
                      {d.customLangline
                        ? t(d.customLangline)
                        : t("dateTime.presets.previous", {
                            value: d.value,
                            unit: t(`dateTime.presets.units.${d.unit}`),
                          })}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="sticky bottom-0 bg-background p-2">
          <Button size="sm" type="submit" className="w-full">
            {t("common.apply")}
          </Button>
        </div>
      </form>
    </Form>
  );
}

type DateRangeName = "from" | "to";
export function DateTimeFormField({
  title,
  name,
  form,
}: {
  title: string;
  name: DateRangeName;
  form: ReturnType<typeof useForm<{ from: Date | string; to: Date | string }>>;
}) {
  const [open, setOpen] = useState(false);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-col">
          <FormLabel>{title}</FormLabel>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <FormControl>
                <div className="flex justify-start align-center">
                  <Input
                    value={normalizeDateValue(field.value)}
                    onChange={(e) => field.onChange(e.target.value)}
                    onBlur={(e) => {
                      const normalized = toFormValue(e.target.value);
                      field.onChange(normalized);
                    }}
                    placeholder="YYYY/MM/DD HH:mm"
                  />
                  <Button
                    variant={"outline"}
                    className={cn(
                      "ml-2 font-normal",
                      !field.value && "text-muted-foreground",
                    )}
                    type="button"
                  >
                    <CalendarIcon className="ml-auto h-4 w-4" />
                  </Button>
                </div>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent
              side="left"
              align="start"
              onOpenAutoFocus={(e) => e.preventDefault()}
              className="w-auto p-0"
            >
              <DateRangePickerCalendar
                date={field.value}
                setDate={(date) => {
                  field.onChange(date);
                }}
              />
            </PopoverContent>
          </Popover>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
